import { InvariantViolation } from './errors';
import type { ConnectorId, OfflineSessionStatus, StopReason } from './types';

export interface OfflineSessionSnapshot {
  readonly queueId: string;
  readonly idTag: string;
  readonly connectorId: ConnectorId;
  readonly startedAt: number;
  readonly duration: number;
  readonly meterStartWh: number;
  readonly energyDeliveredWh: number;
  readonly meterValueIntervalSec: number;
  readonly startSoC?: number;
  readonly endSoC?: number;
  readonly voltage: number;
  readonly stopReason: StopReason;
  readonly status: OfflineSessionStatus;
}

export class OfflineSession {
  readonly queueId: string;
  readonly idTag: string;
  readonly connectorId: ConnectorId;
  readonly startedAt: number;
  readonly duration: number;
  readonly meterStartWh: number;
  readonly energyDeliveredWh: number;
  readonly meterValueIntervalSec: number;
  readonly startSoC: number | null;
  readonly endSoC: number | null;
  readonly voltage: number;
  readonly stopReason: StopReason;
  #status: OfflineSessionStatus;

  constructor(snap: OfflineSessionSnapshot) {
    if (snap.duration <= 0) {
      throw new InvariantViolation('OfflineSession', 'duration > 0', `queueId=${snap.queueId}`);
    }
    if (snap.meterStartWh < 0 || snap.energyDeliveredWh < 0) {
      throw new InvariantViolation(
        'OfflineSession',
        'meter values are non-negative',
        `queueId=${snap.queueId}`,
      );
    }
    if (snap.meterValueIntervalSec <= 0) {
      throw new InvariantViolation(
        'OfflineSession',
        'meterValueIntervalSec > 0',
        `queueId=${snap.queueId}`,
      );
    }
    if (snap.voltage <= 0) {
      throw new InvariantViolation('OfflineSession', 'voltage > 0', `queueId=${snap.queueId}`);
    }
    this.queueId = snap.queueId;
    this.idTag = snap.idTag;
    this.connectorId = snap.connectorId;
    this.startedAt = snap.startedAt;
    this.duration = snap.duration;
    this.meterStartWh = snap.meterStartWh;
    this.energyDeliveredWh = snap.energyDeliveredWh;
    this.meterValueIntervalSec = snap.meterValueIntervalSec;
    this.startSoC = snap.startSoC ?? null;
    this.endSoC = snap.endSoC ?? null;
    this.voltage = snap.voltage;
    this.stopReason = snap.stopReason;
    this.#status = snap.status;
  }

  get status(): OfflineSessionStatus {
    return this.#status;
  }

  setStatus(next: OfflineSessionStatus): void {
    this.#status = next;
  }
}
